

export interface SuburbReview {
  text: string;
  author: string;
  street: string;
  service: string;
}

export interface SuburbConfig {
  name: string;
  postcode: string;
  region: string;
  streets: string[];
  reviews: SuburbReview[];
}

export const SUBURBS_MAP: Record<string, SuburbConfig> = {
  "indooroopilly": {
    name: "Indooroopilly",
    postcode: "4068",
    region: "Western Suburbs",
    streets: ["Grosvenor Rd", "Moggill Rd", "Station Rd", "Musgrave Rd"],
    reviews: [
      { author: "Sarah L.", street: "Grosvenor Rd", service: "MacBook Screen Repair", text: "Ike fixed my cracked MacBook screen on Grosvenor Rd same-day. Incredible service and much cheaper than the mall!" },
      { author: "James T.", street: "Musgrave Rd", service: "Data Recovery", text: "Lost all my wedding photos on an old drive. Ike recovered everything at his Indooroopilly lab. Lifesaver." }
    ]
  },
  "toowong": {
    name: "Toowong",
    postcode: "4066",
    region: "Western Suburbs",
    streets: ["High St", "Sherwood Rd", "Coronation Dr", "Sylvan Rd"],
    reviews: [
      { author: "Michael P.", street: "High St", service: "Senior Tech Support", text: "Expert computer help for seniors here in Toowong. Ike explained the scam protection perfectly on High St." }
    ]
  },
  "taringa": {
    name: "Taringa",
    postcode: "4068",
    region: "Western Suburbs",
    streets: ["Moggill Rd", "Swann Rd", "Gailey Rd"],
    reviews: [
      { author: "David W.", street: "Swann Rd", service: "Gaming PC Build", text: "Best custom PC builder near Taringa. The cable management on my new rig is art. Thanks Ike!" }
    ]
  },
  "kenmore": {
    name: "Kenmore",
    postcode: "4069",
    region: "Western Suburbs",
    streets: ["Moggill Rd", "Brookfield Rd", "Kenmore Rd"],
    reviews: [
      { author: "Elizabeth J.", street: "Brookfield Rd", service: "Laptop Battery Replacement", text: "Fast laptop battery swap in Kenmore. No travel fees and done within 24 hours. Highly recommend." }
    ]
  },
  "west-end": {
    name: "West End",
    postcode: "4101",
    region: "Inner Suburbs",
    streets: ["Boundary St", "Vulture St", "Hardgrave Rd"],
    reviews: [
      { author: "Arjun K.", street: "Boundary St", service: "Liquid Damage Repair", text: "Spilled coffee on my laptop in a West End cafe. Ike saved the logic board. Professional and honest." }
    ]
  },
  "new-farm": {
    name: "New Farm",
    postcode: "4005",
    region: "Inner Suburbs",
    streets: ["Brunswick St", "Merthyr Rd", "James St", "Oxlade Dr"],
    reviews: [
      { author: "Thomas B.", street: "Merthyr Rd", service: "Public Holiday Repair", text: "Ike visited my home on a public holiday! No extra fees and he treated the repair like a puzzle to solve. Friendly and affordable." },
      { author: "Jessica M.", street: "James St", service: "Hinge Repair", text: "Sent a photo of my broken hinge, got a quick quote, and Ike fixed it same-day. 15 years experience really shows." }
    ]
  },
  "chermside": {
    name: "Chermside",
    postcode: "4032",
    region: "Northern Suburbs",
    streets: ["Gympie Rd", "Hamilton Rd", "Rode Rd"],
    reviews: [
      { author: "Linda M.", street: "Rode Rd", service: "Slow Laptop Fix", text: "My laptop was so slow, but Ike's SSD upgrade in Chermside made it feel brand new. Brilliant!" }
    ]
  },
  "sunnybank": {
    name: "Sunnybank",
    postcode: "4109",
    region: "Southern Suburbs",
    streets: ["Mains Rd", "McCullough St", "Turton St"],
    reviews: [
      { author: "Kevin L.", street: "Mains Rd", service: "Workstation Setup", text: "Great workstation setup for my home office in Sunnybank. Professional monitor mounting and speed tuning." }
    ]
  },
  "bulimba": {
    name: "Bulimba",
    postcode: "4171",
    region: "Eastern Suburbs",
    streets: ["Oxford St", "Love St", "Bulimba St"],
    reviews: [
      { author: "Grace H.", street: "Oxford St", service: "Anti-Scam Security", text: "Ike helped me secure my banking near Oxford St after a scary phone scam. I feel much safer now." }
    ]
  },
  "east-brisbane": {
    name: "East Brisbane",
    postcode: "4169",
    region: "Eastern Suburbs",
    streets: ["Lytton Rd", "Stanley St", "Vulture St", "Wellington Rd"],
    reviews: [
      { author: "Daniel K.", street: "Stanley St", service: "Laptop Hinge Repair", text: "Fixed my broken laptop hinge in East Brisbane. Quick turnaround and very solid repair." }
    ]
  },
  "yeronga": {
    name: "Yeronga",
    postcode: "4104",
    region: "Southern Suburbs",
    streets: ["Fairfield Rd", "Hyde Rd", "Kadumba St", "Park Rd"],
    reviews: [
      { author: "Sophie M.", street: "Hyde Rd", service: "MacBook Battery", text: "Ike replaced my MacBook battery. He came to Yeronga and did it on-site. Fantastic service!" }
    ]
  }
};

const KNOWN_SUBURB_SLUGS = new Set([
  "indooroopilly", "taringa", "toowong", "st-lucia", "kenmore", "brookfield", "chapel-hill", "oxley", "corinda", "graceville", "sherwood", "mount-ommaney",
  "brisbane-cbd", "west-end", "new-farm", "highgate-hill", "milton", "paddington", "newstead", "teneriffe", "fortitude-valley", "spring-hill", "south-brisbane",
  "chermside", "everton-park", "stafford", "lutwyche", "windsor", "kedron", "sandgate", "bracken-ridge", "aspley", "nundah",
  "yeronga", "woolloongabba", "sunnybank", "mount-gravatt", "eight-mile-plains", "rocklea", "acacia-ridge", "moorooka", "annerley",
  "east-brisbane", "morningside", "balmoral", "bulimba", "carina", "camp-hill", "wynnum", "manly", "cannon-hill", "tingalpa"
]);

// Fallback generator for suburbs not explicitly detailed above
export const getSuburbConfig = (id: string): SuburbConfig | null => {
  if (SUBURBS_MAP[id]) return SUBURBS_MAP[id];
  
  if (!KNOWN_SUBURB_SLUGS.has(id)) {
    return null;
  }

  // Clean the ID back to a readable name
  const name = id.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  
  return {
    name,
    postcode: "Brisbane",
    region: "Greater Brisbane",
    streets: ["Main St", "Local Rd"],
    reviews: [
      { 
        author: "Verified Local", 
        street: "Local Area", 
        service: "Computer Repair", 
        text: `Excellent same-day service in ${name}. Ike provided a fixed quote and fixed the issue perfectly.` 
      }
    ]
  };
};